// @givmo/board — the chrome a DIRECTOR sees (CBG-SEC2 SEC2-WEB; Jonathan's approved frames, D-CBG-18;
// CBG-UX10's redraw C, D-CBG-40).
//
// TWO FRAMES, chosen by the page's address, never by the page:
//
// ══ A PAGE ABOUT ONE OF THE ACCOUNT'S BOARDS: THE OFFICER BOARD'S FRAME (CBG-UX10) ══════════════
//
// Jonathan, 2026-10-24 12:19 PDT: "why can't it just look almost exactly like the Board admin module,
// just without the capabilities to do things only admins can do?", approved as design C at 14:40 PDT
// (D-CBG-40). So a page about a board this signed-in account holds a seat on (its `?org=` names one)
// wears the officer console's frame (the host console's app shell): the sidebar, drawn by the same
// `OrgSidebarNav` in the officer menu's order, holding only the pages a director has (`directorPages`);
// the board's name and the director's place on it where the officer's frame shows the organization;
// the account foot; the bar naming the app, "Board"; and every page's header in the officer's
// `PageHeader`, at the officer's width. Below `lg` the sidebar is the officer frame's drawer, opened by
// the bar's menu button, the small-screen convention every console page follows.
//
// What it is NOT: the org console. A director is no console member (D-CPO-24): no app grid, no gear,
// no Team, Profile or Settings, no organization switcher. The director's door stays its own behind it:
// the director principal, the narrow reads, closed sessions, the signing window. While `/me` hasn't
// answered, the frame is drawn with its rows to come; a board `/me` doesn't list (no seat, or one a
// refusal took away) gets the column below, which offers no way into it (CBG-FIX34).
//
// ══ EVERYTHING ELSE: THE ONE COLUMN (D-CBG-18) ═════════════════════════════════════════════════
//
// The invitation, the door, the console's own email screens in their director arrival, the move, the
// account's boards and its Sign-in & security (whatever `?org=` says: `ACCOUNT_PAGES`): one 880px
// column under a header carrying the wordmark, the identity line ("Dana Whitfield · Harborlight
// Youth Arts board", or the account's name above any one board), and on the right the signing chip
// while the window is open and the account menu (the name, the address, the way home, "Sign-in &
// security" where its card has something to show, "Sign out"). Signed out, the header carries the
// wordmark alone.
//
// A TAKEOVER, in either frame: something deep in a page (the minutes' Sign, the disclosure dialog's
// Record it) can put Account Security's setup offer in place of the page's own column, under the same
// header, while the page stays mounted behind it — so the act it holds, and anything the director
// typed, are there when the offer ends (`DirectorShellTakeover`, used by `DirectorSetupOfferPage`).

import {
  createContext,
  use,
  useLayoutEffect,
  useMemo,
  useState,
  type MouseEvent,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import {
  Avatar,
  FOCUS_RING,
  GivmoLogomark,
  GivmoWordmark,
  Icon,
  IconButton,
  Menu,
  NAV_DRAWER_TOUCH_TARGET,
  PageHeader,
  cn,
  navDrawerClasses,
  shouldInterceptClick,
  testIdProp,
  useNavDrawer,
  type LucideLikeIcon,
} from '@givmo/design-system';
import { OrgSidebarNav, type Sidebar, type SidebarRow } from '@givmo/org-console';
import {
  BookText,
  CalendarDays,
  FileSignature,
  Handshake,
  KeyRound,
  LayoutGrid,
  LogOut,
  Menu as MenuGlyph,
  Network,
  Users,
  X,
} from 'lucide-react';
import { boardPath, useNavigate, usePathname, useSearch } from '../../board-routing';
import { BOARD_DIRECTOR_PAGES } from '../../board-routes';
import { useBoardBasePath } from '../../board-runtime';
import { useBoardDirectorHost, type BoardDirectorAccount } from '../../director/director-host';
import {
  MEETINGS_TITLE,
  directorBoardSearch,
  directorHomeTitle,
  directorOrgOf,
  seatCommitteeNames,
  wayHome,
} from '../../director/director-board';
import { directorBoardFor, useDirectorMe } from '../../director/director-me';
import { isCommitteeOnly, type DirectorBoard } from '../../director/director-wire';
import { committeeOnlyWords as co } from '../committees/committee-only-words';
import { ownBoardCapacity } from '../roster/officer-roles';
import { useToDoLayout } from '../todo/todo-store';
import { DirectorToDo } from './director-todo';
import { directorPagesWords as w } from './director-pages-words';
import { SigningChip } from './signing-chip';

export interface DirectorShellProps {
  title: ReactNode;
  description?: ReactNode;
  /** Under the wordmark in the one column: who the director is, and which board. */
  identity?: ReactNode;
  actions?: ReactNode;
  testId: string;
  children: ReactNode;
}

type DirectorFrame = 'board' | 'column';

/** Where a page's column can be replaced while the page stays mounted behind it. */
interface DirectorShellTakeoverSlot {
  host: HTMLElement | null;
  /** Hide the page's own column; the returned function gives it back. */
  hold: () => () => void;
  /** The frame the page wears, so the replacement's header is the frame's. */
  frame: DirectorFrame;
}

const DirectorShellTakeoverContext = createContext<DirectorShellTakeoverSlot | null>(null);

/** The board frame's sidebar, its id for the menu button's `aria-controls`. */
const SIDEBAR_ID = 'director-sidebar';
/** Where the skip link sends the focus. */
const MAIN_CONTENT_ID = 'director-main-content';

/**
 * The account's own pages: home (its boards), the move and Sign-in & security. Each keeps the one
 * column whatever `?org=` says: the move's start always carries its board (`seatMoveSearch`), and
 * the move's "Not now" can bring home one (review-291's F1).
 */
const ACCOUNT_PAGES: readonly string[] = [
  BOARD_DIRECTOR_PAGES.home,
  BOARD_DIRECTOR_PAGES.move,
  BOARD_DIRECTOR_PAGES.signInSecurity,
];

/**
 * The frame a page wears: the officer Board's for a page about a board this account holds a seat on
 * (its `?org=` names one, and `/me` lists it or hasn't answered), the one column for the account's
 * own pages and anything else. The page decides, never its address alone.
 */
function useDirectorFrame(account: BoardDirectorAccount | null): {
  frame: DirectorFrame;
  orgRef: string | null;
  board: DirectorBoard | null;
  multiSeatHome: boolean;
} {
  const orgRef = directorOrgOf(useSearch());
  const meQuery = useDirectorMe();
  const pathname = usePathname();
  const basePath = useBoardBasePath();
  const board = directorBoardFor(meQuery.data, orgRef);
  // The rendered page's path carries the Board mount (`/board/director/…`).
  const accountPage = ACCOUNT_PAGES.some(
    (page) => pathname === `${basePath}${page}` || pathname === `${basePath}${page}/`,
  );
  const frame =
    account !== null && orgRef !== null && !accountPage && (meQuery.isPending || board !== null)
      ? 'board'
      : 'column';
  // CBG-FIX151: the multi-seat home names every board, so it carries no one board's to-do list,
  // whatever its `?org=` says (the move's "Not now" brings it home with one); the move's own page
  // may keep its board's.
  const home = BOARD_DIRECTOR_PAGES.home;
  const multiSeatHome = pathname === `${basePath}${home}` || pathname === `${basePath}${home}/`;
  return { frame, orgRef, board, multiSeatHome };
}

export function DirectorShell({
  title,
  description,
  identity,
  actions,
  testId,
  children,
}: DirectorShellProps) {
  const { account } = useBoardDirectorHost();
  const { frame, orgRef, board, multiSeatHome } = useDirectorFrame(account);
  const [host, setHost] = useState<HTMLDivElement | null>(null);
  const [holds, setHolds] = useState(0);
  const takeover = useMemo<DirectorShellTakeoverSlot>(
    () => ({
      host,
      hold: () => {
        setHolds((count) => count + 1);
        return () => setHolds((count) => count - 1);
      },
      frame,
    }),
    [host, frame],
  );

  // CBG-UX7: the director's to-do list never covers what the page needs. Open on a wide screen, it
  // takes its 360px and its 24px gutter from the page; minimized, the page's foot keeps the tab's
  // 44px and a margin clear (frame t1).
  const { shown, docked } = useToDoLayout('director');

  // The page's own column: hidden, never unmounted, while a takeover holds it.
  const column = (heading: ReactNode) => (
    <>
      <div className={holds > 0 ? 'hidden' : 'contents'}>
        {heading}
        <DirectorShellTakeoverContext value={takeover}>
          {children}
          {/* CBG-UX7: the director's to-do list, on every page about one of their boards, after
              the page's own content; inside the takeover slot, so FIX74's Open can offer the
              setup in the column, and hidden with the column while a takeover holds it. Never on
              the multi-seat home (CBG-FIX151). */}
          {multiSeatHome ? null : <DirectorToDo />}
        </DirectorShellTakeoverContext>
      </div>
      <div ref={setHost} className="contents" />
    </>
  );

  if (frame === 'board' && account !== null) {
    return (
      <DirectorBoardFrame account={account} orgRef={orgRef} board={board} testId={testId}>
        <div
          className={cn(
            'mx-auto flex w-full max-w-[1120px] flex-col gap-24 p-24',
            docked && 'xl:mr-[384px] xl:w-auto',
            shown && 'pb-[72px]',
          )}
        >
          {column(<PageHeader title={title} subtitle={description} actions={actions} />)}
        </div>
      </DirectorBoardFrame>
    );
  }

  return (
    <div
      className={cn(
        'min-h-screen bg-surface-sunken',
        shown && 'pb-[72px]',
        docked && 'xl:pr-[384px]',
      )}
      {...testIdProp(testId)}
    >
      <header className="border-b border-border-subtle bg-surface">
        <div className="mx-auto flex w-full max-w-[880px] flex-col gap-4 px-24 py-14">
          <div className="flex min-h-32 items-center justify-between gap-12">
            <GivmoWordmark className="h-[20px] w-auto shrink-0 text-brand-500" aria-label="Givmo" />
            {account !== null ? (
              <div className="flex min-w-0 items-center gap-8">
                <SigningChip />
                <AccountMenu account={account} />
              </div>
            ) : null}
          </div>
          {identity ? (
            <span
              className="truncate font-sans text-s13 font-w400 tracking-givmo text-text-tertiary"
              {...testIdProp('director-identity')}
            >
              {identity}
            </span>
          ) : null}
        </div>
      </header>

      <main className="mx-auto flex w-full max-w-[880px] flex-col gap-20 px-24 py-32">
        {column(<DirectorPageHeading title={title} description={description} actions={actions} />)}
      </main>
    </div>
  );
}

// ── The board frame: the officer's, holding only a director's pages ──────────────────────────────

interface DirectorPage {
  id: string;
  label: string;
  icon: LucideLikeIcon;
  to: string;
  /** The pages beneath it that light it. */
  under: readonly string[];
}

/**
 * A director's pages, in the officer menu's own order and words (`board-nav.ts`; D-CBG-9 as amended,
 * D-CBG-35, D-CBG-40). Board members and Conflicts of interest are the board's, so a seat for someone
 * who isn't on the board has neither (CBG-YR6-OUTSIDE); Committees shows while the seat sits on one.
 */
export function directorPages(board: DirectorBoard | null): DirectorPage[] {
  const outside = isCommitteeOnly(board);
  const pages: DirectorPage[] = [
    {
      id: 'meetings',
      label: MEETINGS_TITLE,
      icon: CalendarDays,
      to: BOARD_DIRECTOR_PAGES.meetings,
      under: [BOARD_DIRECTOR_PAGES.meetings, '/director/packets'],
    },
    {
      id: 'consents',
      label: w.writtenConsents,
      icon: FileSignature,
      to: BOARD_DIRECTOR_PAGES.consents,
      under: [BOARD_DIRECTOR_PAGES.consents],
    },
    {
      id: 'records',
      // A committee seat's records page is its committees' ("Committee records").
      label: outside ? co.recordsTitle : w.minuteBook,
      icon: BookText,
      to: BOARD_DIRECTOR_PAGES.records,
      under: [BOARD_DIRECTOR_PAGES.records, '/director/returns'],
    },
  ];
  if (!outside) {
    // Board members lights on its own page alone. The member's own checklist and About the board,
    // the checklist's page, sit under no row: none opens them (the to-do list does), so none is
    // lit there (review-291's M5b).
    pages.push({
      id: 'members',
      label: w.membersTitle,
      icon: Users,
      to: BOARD_DIRECTOR_PAGES.members,
      under: [BOARD_DIRECTOR_PAGES.members],
    });
  }
  if (seatCommitteeNames(board).length > 0) {
    pages.push({
      id: 'committees',
      label: w.committeesTitle,
      icon: Network,
      to: BOARD_DIRECTOR_PAGES.committees,
      under: [BOARD_DIRECTOR_PAGES.committees],
    });
  }
  if (!outside) {
    pages.push({
      id: 'conflicts',
      label: w.conflictsTitle,
      icon: Handshake,
      to: BOARD_DIRECTOR_PAGES.disclosures,
      under: [BOARD_DIRECTOR_PAGES.disclosures],
    });
  }
  return pages;
}

function DirectorBoardFrame({
  account,
  orgRef,
  board,
  testId,
  children,
}: {
  account: BoardDirectorAccount;
  orgRef: string | null;
  board: DirectorBoard | null;
  testId: string;
  children: ReactNode;
}) {
  const { open, toggle, close, drawerRef, triggerRef } = useNavDrawer();
  const pathname = usePathname();
  const basePath = useBoardBasePath();
  const navigate = useNavigate();
  const meQuery = useDirectorMe();
  // The mark means "my board": the way home, as the account menu's row says it — a mark, never the
  // page's current link, as the officer frame's wordmark is.
  const home = wayHome(meQuery.data, directorHomeTitle(meQuery));
  const homeHref = `${boardPath(basePath, home.to)}${
    home.search === undefined ? '' : `?${new URLSearchParams(home.search).toString()}`
  }`;
  const onMark = (event: MouseEvent<HTMLAnchorElement>) => {
    close();
    if (!shouldInterceptClick(event)) return;
    event.preventDefault();
    void navigate({ to: home.to, search: home.search });
  };
  const search = orgRef === null ? undefined : directorBoardSearch(orgRef);
  const pages = directorPages(board);
  const hrefOf = (page: DirectorPage) =>
    `${basePath}${page.to}${orgRef === null ? '' : `?org=${encodeURIComponent(orgRef)}`}`;

  const rows: SidebarRow<LucideLikeIcon>[] = pages.map((page) => ({
    id: page.id,
    label: page.label,
    icon: page.icon,
    href: hrefOf(page),
    external: false,
    state: 'link',
    requires: 'board:read',
    // The rendered page's path carries the Board mount (`/board/director/…`).
    active: page.under.some((prefix) => pathname.startsWith(`${basePath}${prefix}`)),
    testId: `director-menu-${page.id}`,
  }));
  // One section draws no caption: its label is only the list's accessible name (OM-27).
  const sidebar: Sidebar<LucideLikeIcon> = {
    title: '',
    app: w.appWord,
    sections: [{ id: 'board', label: w.appWord, rows }],
    captions: 'hidden',
  };

  return (
    <div className="flex min-h-screen bg-surface-sunken" {...testIdProp(testId)}>
      <a
        href={`#${MAIN_CONTENT_ID}`}
        className="sr-only rounded-md bg-surface font-sans text-s14 font-w600 tracking-givmo text-brand-700 shadow-ring focus:not-sr-only focus:absolute focus:left-16 focus:top-16 focus:z-50 focus:px-16 focus:py-8"
        {...testIdProp('director-skip-to-content')}
      >
        Skip to main content
      </a>

      {/* The sidebar on desktop and, below `lg`, the drawer the bar's menu button opens: one element,
          the officer frame's (`navDrawerClasses`), a `div` with a role because the open drawer is
          a modal. */}
      <div
        ref={drawerRef}
        id={SIDEBAR_ID}
        className={navDrawerClasses(open)}
        role={open ? 'dialog' : 'navigation'}
        aria-modal={open ? true : undefined}
        aria-label={open ? 'Navigation' : 'Primary'}
        {...testIdProp('director-sidebar')}
      >
        <div className="flex h-[56px] items-center justify-between gap-8 px-16">
          <a
            href={homeHref}
            onClick={onMark}
            className={cn('flex min-h-44 items-center gap-8 rounded-md outline-none', FOCUS_RING)}
            {...testIdProp('director-brand')}
          >
            <GivmoWordmark className="h-[22px] w-auto text-brand-500" aria-label="Givmo" />
          </a>
          <button
            type="button"
            onClick={close}
            aria-label="Close navigation"
            className={`-mr-12 ${NAV_DRAWER_TOUCH_TARGET}`}
            {...testIdProp('director-nav-close')}
          >
            <Icon icon={X} size={20} aria-hidden />
          </button>
        </div>

        {board !== null ? <BoardChip board={board} /> : null}

        {orgRef !== null && board !== null ? (
          <OrgSidebarNav
            sidebar={sidebar}
            onNavigate={(href) => {
              const page = pages.find((candidate) => hrefOf(candidate) === href);
              if (page !== undefined) void navigate({ to: page.to, search });
            }}
            onRowActivate={close}
            className="flex-1 overflow-y-auto px-12 py-12"
          />
        ) : (
          <div className="flex-1" />
        )}

        <AccountFoot account={account} />
      </div>

      {/* `inert` while the drawer is open: everything behind a modal is unreachable. */}
      <div className="flex min-w-0 flex-1 flex-col" inert={open}>
        <header className="sticky top-0 z-10 flex h-[56px] shrink-0 items-center justify-between gap-8 border-b border-border-subtle bg-surface px-16 md:gap-12 lg:px-24">
          <div className="flex min-w-0 items-center gap-6">
            <button
              ref={triggerRef}
              type="button"
              onClick={toggle}
              aria-label={open ? 'Close navigation' : 'Open navigation'}
              aria-expanded={open}
              aria-controls={SIDEBAR_ID}
              className={`-ml-12 ${NAV_DRAWER_TOUCH_TARGET}`}
              {...testIdProp('director-nav-toggle')}
            >
              <Icon icon={MenuGlyph} size={20} aria-hidden />
            </button>
            {/* The G logomark on a phone and a tablet, where the sidebar is a closed drawer. */}
            <a
              href={homeHref}
              onClick={onMark}
              className={cn(
                'flex min-h-44 min-w-44 shrink-0 items-center justify-center rounded-md outline-none lg:hidden',
                FOCUS_RING,
              )}
              {...testIdProp('director-brand-compact')}
            >
              <GivmoLogomark className="h-[22px] w-auto text-brand-500" />
            </a>
            <span className="truncate font-sans text-s13 font-w600 tracking-normal text-text-secondary">
              {w.appWord}
            </span>
          </div>
          <div className="flex min-w-0 items-center gap-8">
            <SigningChip />
            <AccountMenu account={account} />
          </div>
        </header>

        <main id={MAIN_CONTENT_ID} tabIndex={-1} className="min-w-0 flex-1 outline-none">
          {children}
        </main>
      </div>

      {/* The drawer's barrier, mounted only while open; `onMouseDown` keeps it from taking the focus
          (the hook's Escape reads the focus). */}
      {open ? (
        <button
          type="button"
          tabIndex={-1}
          onClick={close}
          onMouseDown={(event) => event.preventDefault()}
          aria-label="Dismiss navigation"
          className="fixed inset-0 z-40 bg-scrim lg:hidden"
          {...testIdProp('director-nav-backdrop')}
        />
      ) : null}
    </div>
  );
}

/**
 * Where the officer's frame shows the organization (the org switcher's chip): the board and the
 * director's place on it, "Director · Treasurer" (`ownBoardCapacity`, the boards list's approved
 * words), or a committee-only seat's committees.
 */
function BoardChip({ board }: { board: DirectorBoard }) {
  const name = board.organization_name?.trim() || 'Your board';
  const place = isCommitteeOnly(board)
    ? seatCommitteeNames(board).join(' · ')
    : ownBoardCapacity(board);
  return (
    <div className="flex flex-col px-12 pt-8" {...testIdProp('director-board-chip')}>
      <div className="flex w-full items-center gap-10 rounded-lg border border-border-subtle bg-surface px-10 py-8">
        <Avatar name={name} size="sm" />
        <span className="min-w-0 flex-1">
          <span className="block truncate font-sans text-s13 font-w600 tracking-givmo text-text-strong">
            {name}
          </span>
          {place.length > 0 ? (
            <span className="block truncate font-sans text-s12 font-w400 tracking-givmo text-text-tertiary">
              {place}
            </span>
          ) : null}
        </span>
      </div>
    </div>
  );
}

/** The officer frame's account foot: identity and sign-out only (Org Machinery OM-22). */
function AccountFoot({ account }: { account: BoardDirectorAccount }) {
  const { signOut } = useBoardDirectorHost();
  const name = account.displayName ?? account.email ?? '';
  return (
    <div className="border-t border-border-subtle p-12">
      <div className="flex items-center gap-10 rounded-md p-8">
        <Avatar name={name} size="sm" />
        <span className="min-w-0 flex-1">
          <span className="block truncate font-sans text-s13 font-w600 tracking-givmo text-text-strong">
            {name}
          </span>
          {account.email && account.displayName ? (
            <span className="block truncate font-sans text-s12 font-w400 tracking-givmo text-text-tertiary">
              {account.email}
            </span>
          ) : null}
        </span>
        <IconButton
          aria-label="Sign out"
          tone="ghost"
          className="size-44 lg:size-36"
          onClick={() => void signOut()}
          testId="director-sign-out"
        >
          <Icon icon={LogOut} size={18} aria-hidden />
        </IconButton>
      </div>
    </div>
  );
}

/** The one column's title, description and actions. */
function DirectorPageHeading({
  title,
  description,
  actions,
}: {
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <div className="flex flex-wrap items-start justify-between gap-16">
      <div className="flex min-w-0 flex-col gap-6">
        <h1 className="font-sans text-s24 font-w700 tracking-givmo text-text-strong">{title}</h1>
        {description ? (
          <p className="font-sans text-s15 font-w400 leading-h15 tracking-givmo text-text-secondary">
            {description}
          </p>
        ) : null}
      </div>
      {actions}
    </div>
  );
}

/**
 * A page of its own in the director shell — or, rendered inside a page that already has one, in
 * place of that page's column (see the header), under that frame's own heading. Its root carries
 * `testId` either way.
 */
export function DirectorShellTakeover({
  title,
  description,
  identity,
  testId,
  children,
}: Omit<DirectorShellProps, 'actions'>) {
  const slot = use(DirectorShellTakeoverContext);
  // Before paint, so the page and its replacement never show together for a frame.
  useLayoutEffect(() => slot?.hold(), [slot]);

  if (slot === null) {
    return (
      <DirectorShell title={title} description={description} identity={identity} testId={testId}>
        {children}
      </DirectorShell>
    );
  }
  if (slot.host === null) return null;
  return createPortal(
    <div className="contents" {...testIdProp(testId)}>
      {slot.frame === 'board' ? (
        <PageHeader title={title} subtitle={description} />
      ) : (
        <DirectorPageHeading title={title} description={description} />
      )}
      {children}
    </div>,
    slot.host,
  );
}

/**
 * The console's account menu, its director rows: the account, the way home, Account Security's
 * "Sign-in & security" (only where its card has something to show, as on the console's own menu),
 * and "Sign out". What the row rests on is read as the director reaches for the menu (AS-WEB16).
 * CBG-FIX104 (row 4.2): the home row is home's own title (`directorHomeTitle`), as the way back
 * is: "Your committees" while every seat the account holds is a committee's, "Your boards and
 * committees" for an account holding none (review-233's F3). For an account home lands on one seat
 * for (CBG-FIX109), the row opens that seat's page and says so, "Meetings" (`wayHome`).
 */
function AccountMenu({ account }: { account: BoardDirectorAccount }) {
  const { signOut, signInSecurity } = useBoardDirectorHost();
  const navigate = useNavigate();
  const meQuery = useDirectorMe();
  const home = wayHome(meQuery.data, directorHomeTitle(meQuery));
  const name = account.displayName ?? account.email ?? '';

  return (
    <Menu.Root
      onOpenChange={(opening) => {
        if (opening) signInSecurity?.read();
      }}
    >
      <Menu.Trigger
        render={
          <IconButton
            aria-label="Account menu"
            tone="ghost"
            className="size-44 shrink-0 rounded-full lg:size-32"
            testId="director-account-menu"
          />
        }
        onPointerEnter={() => signInSecurity?.read()}
        onFocus={() => signInSecurity?.read()}
      >
        <Avatar name={name} size="sm" />
      </Menu.Trigger>
      <Menu.Content saas side="bottom" align="end" className="min-w-[212px]">
        <div className="px-12 py-8">
          {account.displayName ? (
            <p className="truncate font-sans text-s13 font-w600 tracking-givmo text-text-strong">
              {account.displayName}
            </p>
          ) : null}
          {account.email ? (
            <p className="truncate font-sans text-s12 font-w400 tracking-givmo text-text-tertiary">
              {account.email}
            </p>
          ) : null}
        </div>
        <Menu.Separator />
        <Menu.Item
          saas
          startIcon={<Icon icon={LayoutGrid} size={16} className="text-icon" aria-hidden />}
          onClick={() => void navigate({ to: home.to, search: home.search })}
          testId="director-account-boards"
        >
          {home.words}
        </Menu.Item>
        {signInSecurity?.available ? (
          <Menu.Item
            saas
            startIcon={<Icon icon={KeyRound} size={16} className="text-icon" aria-hidden />}
            onClick={() => void navigate({ to: BOARD_DIRECTOR_PAGES.signInSecurity })}
            testId="director-account-sign-in-security"
          >
            {signInSecurity.title}
          </Menu.Item>
        ) : null}
        <Menu.Item
          saas
          startIcon={<Icon icon={LogOut} size={16} className="text-icon" aria-hidden />}
          onClick={() => void signOut()}
          testId="director-account-sign-out"
        >
          Sign out
        </Menu.Item>
      </Menu.Content>
    </Menu.Root>
  );
}
